import type { AxiosError } from "axios";
import router from "@/router";
import store from "@/store";
import { LOGIN_ROUTE, SUBSCRIPTION_WALL_ROUTE } from "@/router/routes";
import { isAuthOnlyRoute } from "@/router/auth-only-routes";
import { LOGOUT, UPDATE_ERRORMSG } from "@/store/action-types";
import { useSubscriptionModals } from "@/composables/ui/useSubscriptionModals";
import { SERVER_ERR_MSG, toastService } from "@/utils/const";

/**
 * Handles HTTP status code errors (400, 402, 403, 404, 409, 422, 500)
 * Returns { absorbed: true } if the error was fully handled and should not propagate
 */
interface ErrorResponseData {
  message?: string;
  error?: string;
  code?: string;
  trialEndsAt?: string;
}

export function handleHttpStatusError(error: AxiosError): { absorbed: boolean } {
  if (!error.response) return { absorbed: false };

  const status = error.response.status;
  const url = error.config?.url || "";
  const responseData = error.response.data as ErrorResponseData | undefined;
  const backendMessage = responseData?.message;

  switch (status) {
    case 400:
      handleBadRequest(error, url, backendMessage);
      return { absorbed: false };

    case 402:
      return handlePaymentRequired(error);

    case 403:
      handleForbidden(error, responseData, backendMessage);
      return { absorbed: false };

    case 404:
      handleNotFound(error, url, backendMessage);
      return { absorbed: false };

    case 409:
      handleConflict(error, backendMessage);
      return { absorbed: false };

    case 422:
      handleUnprocessableEntity(error, url, backendMessage);
      return { absorbed: false };

    case 500:
      handleInternalServerError(error, url, backendMessage);
      return { absorbed: false };

    default:
      return { absorbed: false };
  }
}

/**
 * Handle 400 Bad Request
 */
function handleBadRequest(error: AxiosError, url: string, backendMessage?: string): void {
  const isLoginEndpoint = url.includes("/auth/login");
  const isSMSEndpoint = url.includes("/twilio/send-sms");
  // Promo code errors are handled inline in the component
  const isPromoCodeEndpoint = url.includes("/promo-code/");

  if (!isLoginEndpoint && !isSMSEndpoint && !isPromoCodeEndpoint) {
    store.dispatch(UPDATE_ERRORMSG, {
      errorMsg: backendMessage || SERVER_ERR_MSG,
    });
  }
}

function handlePaymentRequired(error: AxiosError): { absorbed: boolean } {
  const responseData = error.response?.data as ErrorResponseData | undefined;
  const currentRouteName = router.currentRoute.value?.name as string | undefined;
  const currentPath = router.currentRoute.value?.path || "";

  if (isAuthOnlyRoute(currentRouteName, currentPath)) {
    return { absorbed: true };
  }

  // Unauthenticated users should never be sent to the subscription wall
  const isAuthenticated = !!store.getters.sessionToken && !!store.getters.account?.data;
  if (!isAuthenticated) {
    store.dispatch(LOGOUT).then(() => {
      router.push({ name: LOGIN_ROUTE });
    });
    return { absorbed: true };
  }

  if (store.getters.isInternalUser) return { absorbed: true };

  if (responseData?.error === "payment_required") {
    router.push({ name: SUBSCRIPTION_WALL_ROUTE });
    return { absorbed: true };
  }

  if (responseData?.error === "trial_expired") {
    store.dispatch(UPDATE_ERRORMSG, {
      errorMsg:
        responseData.message ||
        "Your free trial has ended. Subscribe to continue using SalesBuckets.",
    });
    router.push({ name: SUBSCRIPTION_WALL_ROUTE });
    return { absorbed: true };
  }

  const recentPurchaseAt = sessionStorage.getItem("sb_credits_just_purchased");
  const isRecentPurchase = recentPurchaseAt && Date.now() - Number(recentPurchaseAt) < 60_000;

  if (responseData?.error === "insufficient_credits") {
    const skipModal = (error.config as any)?._skipCreditModal === true;
    if (!skipModal && !isRecentPurchase) {
      const { showInsufficientCreditsModal } = useSubscriptionModals();
      showInsufficientCreditsModal(
        responseData.message || "Your credit balance is too low. Purchase credits to continue."
      );
    }
    error.absorbedButRecoverable = true;
    return { absorbed: false };
  }

  if (responseData?.error === "no_payment_method") {
    return { absorbed: false }; // Let CreditPurchaseModal handle it inline, no generic toast
  }

  if (responseData?.error === "payment_method_required") {
    if (!isRecentPurchase) {
      const { showUsageBasedFeaturesModal } = useSubscriptionModals();
      showUsageBasedFeaturesModal({
        hasPaymentMethod: false,
        onAddPaymentMethod: null, // modal handles checkout directly
        onProceed: null,
        onPurchaseCredits: null,
      });
    }
    return { absorbed: true };
  }

  if (responseData?.error === "no_access") {
    router.push({ name: SUBSCRIPTION_WALL_ROUTE });
    return { absorbed: true };
  }

  if (responseData?.error === "subscription_inactive") {
    const { showInsufficientCreditsModal } = useSubscriptionModals();
    showInsufficientCreditsModal(
      responseData.message ||
        "Your subscription is inactive. Update your billing to continue using usage-based features.",
      "Subscription Inactive",
      "Update Billing",
      false,
      "/settings/billing"
    );
    if (!router.currentRoute.value?.path?.startsWith("/settings/billing")) {
      router.push({ path: "/settings/billing" });
    }
    return { absorbed: true };
  }

  if (responseData?.error === "feature_locked") {
    const { showInsufficientCreditsModal } = useSubscriptionModals();
    showInsufficientCreditsModal(
      responseData.message || "This feature is available on paid plans. Upgrade to unlock it.",
      "Upgrade Required",
      "Upgrade Plan",
      false,
      "/settings/billing"
    );
    return { absorbed: true };
  }

  store.dispatch(UPDATE_ERRORMSG, {
    errorMsg: responseData?.message || SERVER_ERR_MSG,
  });
  return { absorbed: false };
}

let isRedirectingFrom403 = false;

router.afterEach((to) => {
  if (to.name === LOGIN_ROUTE) {
    isRedirectingFrom403 = false;
  }
});

function isAccountDeactivatedError(
  responseData: ErrorResponseData | undefined,
  backendMessage?: string
): boolean {
  if (responseData?.code === "ACCOUNT_DEACTIVATED") return true;
  if (typeof backendMessage === "string") {
    const lower = backendMessage.toLowerCase();
    return lower.includes("deactivated") || lower.includes("deleted");
  }
  return false;
}

/**
 * Handle 403 Forbidden
 */
function handleForbidden(
  error: AxiosError,
  responseData: ErrorResponseData | undefined,
  backendMessage?: string
): void {
  const url = error.config?.url || "";
  const isAccountDeactivated = isAccountDeactivatedError(responseData, backendMessage);

  if (url.includes("/billing/") && !isAccountDeactivated) {
    // If user is no longer authenticated (e.g. just signed out), redirect to login
    const isAuthenticated = !!store.getters.sessionToken && !!store.getters.account?.data;
    if (!isAuthenticated && !isRedirectingFrom403) {
      isRedirectingFrom403 = true;
      store.dispatch(LOGOUT).then(() => {
        router.push({ name: LOGIN_ROUTE });
      });
    }
    return;
  }

  if (isRedirectingFrom403) {
    return;
  }
  isRedirectingFrom403 = true;

  const toastMessage = isAccountDeactivated
    ? "Your account has been deactivated or deleted. You have been logged out."
    : backendMessage || SERVER_ERR_MSG;

  toastService.error(toastMessage);

  store.dispatch(UPDATE_ERRORMSG, {
    errorMsg: backendMessage || SERVER_ERR_MSG,
  });
  store.dispatch(LOGOUT);
  router.push({ name: LOGIN_ROUTE });
}

/**
 * Handle 404 Not Found
 */
function handleNotFound(error: AxiosError, url: string, backendMessage?: string): void {
  const isUnipileChatByContactEndpoint = url.includes("/unipile/chats/by-contact");
  const isUnipileUsersProfileEndpoint = /\/unipile\/users\/[^\/]+(\?|$)/.test(url);
  // Promo code errors are handled inline in the component
  const isPromoCodeEndpoint = url.includes("/promo-code/");

  if (!isUnipileChatByContactEndpoint && !isUnipileUsersProfileEndpoint && !isPromoCodeEndpoint) {
    store.dispatch(UPDATE_ERRORMSG, {
      errorMsg: backendMessage || SERVER_ERR_MSG,
    });
  }
}

/**
 * Handle 409 Conflict
 */
function handleConflict(error: AxiosError, backendMessage?: string): void {
  store.dispatch(UPDATE_ERRORMSG, {
    errorMsg: backendMessage || SERVER_ERR_MSG,
  });
}

/**
 * Handle 422 Unprocessable Entity
 */
function handleUnprocessableEntity(error: AxiosError, url: string, backendMessage?: string): void {
  const isUnipileChatByContactEndpoint = url.includes("/unipile/chats/by-contact");
  const isUnipileUsersProfileEndpoint = /\/unipile\/users\/[^\/]+(\?|$)/.test(url);

  if (!isUnipileChatByContactEndpoint && !isUnipileUsersProfileEndpoint) {
    store.dispatch(UPDATE_ERRORMSG, {
      errorMsg: backendMessage || SERVER_ERR_MSG,
    });
  }
}

/**
 * Handle 500 Internal Server Error
 */
function handleInternalServerError(error: AxiosError, url: string, backendMessage?: string): void {
  const isSMSEndpoint = url.includes("/twilio/send-sms");

  if (!isSMSEndpoint) {
    store.dispatch(UPDATE_ERRORMSG, {
      errorMsg: backendMessage || SERVER_ERR_MSG,
    });
  }
}
